import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Search,
  Plus,
  Trash2,
  Building2,
  Package2,
  ClipboardList,
} from "lucide-react";

import { authFetch } from "../utils/authFetch";

const splitSpecification = (value = "") => {
  const [itemName, ...descriptionParts] = String(value).split(" - ");
  return { itemName, description: descriptionParts.join(" - ") };
};

function RFQPage() {
  const location = useLocation();
  const [vendors, setVendors] = useState([]);
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [ccEmails, setCcEmails] = useState("");
  const [suggestions, setSuggestions] = useState({});
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [items, setItems] = useState([
    {
    item_name: "",
    brand: "",
    availableBrands: [],
    uom: "Nos",
    image: null,
    preview: "",
  },
  ]);

  // Unified Dashboard UI Theme Matching Tokens
  const t = {
    bg: "#f8fafc",          
    card: "#ffffff",        
    border: "#e2e8f0",      
    borderLight: "#f1f5f9", 
    text: "#0f172a",        
    muted: "#64748b",       
    primary: "#2563eb",     
    dangerText: "#ef4444",  
    dangerBg: "#fef2f2",
  };

  const s = {
    page: {
      minHeight: "100vh",
      backgroundColor: t.bg,
      padding: "32px 40px",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      color: t.text,
    },
    wrap: {
      width: "100%",
      position: "relative"
    },
    header: {
      marginBottom: "28px",
    },
    title: {
      fontSize: "24px",
      fontWeight: "700",
      color: t.text,
      letterSpacing: "-0.01em",
    },
    sub: {
      fontSize: "13px",
      color: t.muted,
      marginTop: "4px",
    },
    section: {
      background: t.card,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
      marginBottom: "24px",
      overflow: "visible",
    },
    sectionHead: {
      padding: "16px 24px",
      borderBottom: `1px solid ${t.border}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    sectionTitle: {
      fontSize: "15px",
      fontWeight: "700",
      color: t.text,
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    sectionSub: {
      fontSize: "12px",
      color: t.muted,
      marginTop: "2px",
    },
    body: {
      padding: "20px 24px",
    },
    label: {
      display: "block",
      fontSize: "12px",
      fontWeight: "600",
      color: t.muted,
      marginBottom: "6px",
    },
    input: {
      width: "100%",
      height: "38px",
      borderRadius: "6px",
      border: `1px solid ${t.border}`,
      padding: "0 12px",
      outline: "none",
      fontSize: "13px",
      fontFamily: "inherit",
      boxSizing: "border-box",
      background: "#ffffff",
      color: t.text,
    },
    select: {
      width: "100%",
      height: "38px",
      borderRadius: "6px",
      border: `1px solid ${t.border}`,
      padding: "0 12px",
      outline: "none",
      fontSize: "13px",
      fontFamily: "inherit",
      boxSizing: "border-box",
      background: "#ffffff",
      color: t.text,
      appearance: "none",
      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 12px center",
      backgroundSize: "14px",
    },
    tableWrapper: {
      border: `1px solid ${t.border}`,
      borderRadius: "8px",
      overflow: "hidden",
      backgroundColor: "#ffffff",
    },
    tableHeader: {
  display: "grid",
  // Changed "2.3fr 1.2fr" to "2.8fr 0.7fr" (Material gets more, Brand gets less)
  gridTemplateColumns: "50px 2.8fr 0.7fr 1fr 1fr 140px 60px", 
  gap: "16px",
  padding: "12px 20px",
  background: "#f8fafc",
  borderBottom: `1px solid ${t.border}`,
  fontSize: "11px",
  fontWeight: "600",
  color: t.muted,
  textTransform: "uppercase",
  alignItems: "center",
},
tableRow: {
  display: "grid",
  // Ensure this matches the header exactly
  gridTemplateColumns: "50px 2.8fr 0.7fr 1fr 1fr 140px 60px",
  gap: "16px",
  padding: "12px 20px",
  alignItems: "center", // This keeps items vertically centered
  borderBottom: `1px solid ${t.borderLight}`,
},
    scrollContainer: {
      maxHeight: "322px", 
      overflowY: "auto",
      overflowX: "hidden",
    },
    compactInput: {
      width: "100%",
      height: "34px",
      borderRadius: "6px",
      border: `1px solid ${t.border}`,
      padding: "0 10px",
      outline: "none",
      fontSize: "13px",
      boxSizing: "border-box",
      background: "#ffffff",
      color: t.text,
    },
    compactSelect: {
      width: "100%",
      height: "34px",
      borderRadius: "6px",
      border: `1px solid ${t.border}`,
      padding: "0 10px",
      outline: "none",
      fontSize: "13px",
      boxSizing: "border-box",
      background: "#ffffff",
      color: t.text,
      appearance: "none",
      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 10px center",
      backgroundSize: "12px",
    },
    rowNumber: {
      fontSize: "12px",
      color: t.muted,
      fontWeight: "500",
      textAlign: "left",
      paddingLeft: "4px",
    },
    compactRemoveBtn: {
      border: "none",
      background: "transparent",
      color: t.dangerText,
      width: "32px",
      height: "32px",
      borderRadius: "6px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.15s ease",
    },
    primaryBtn: {
      height: "36px",
      border: "none",
      background: "#000000", 
      color: "#ffffff",
      borderRadius: "6px",
      padding: "0 14px",
      fontWeight: "600",
      fontSize: "13px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    vendorGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: "16px",
    },
    vendorCard: {
      borderRadius: "10px",
      padding: "14px 16px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      transition: "all 0.15s ease",
    },
    vendorAvatar: {
      width: "36px",
      height: "36px",
      borderRadius: "6px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "600",
      fontSize: "12px",
    },
    checkbox: {
      width: "16px",
      height: "16px",
      accentColor: t.primary,
      cursor: "pointer",
    },
    bottomBar: {
      position: "sticky",
      bottom: "24px",
      background: "#0f172a",
      color: "#ffffff",
      borderRadius: "12px",
      padding: "16px 24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "32px",
      boxShadow: "0 10px 15px -3px rgba(15,23,42,0.3)",
      zIndex: 100,
    },
    submitBtn: {
      height: "38px",
      border: "none",
      background: t.primary,
      color: "#ffffff",
      borderRadius: "6px",
      padding: "0 20px",
      fontWeight: "600",
      fontSize: "14px",
      cursor: "pointer",
      boxShadow: "0 2px 4px rgba(37, 99, 235, 0.2)",
    },
  };

  useEffect(() => {
   authFetch(`${process.env.REACT_APP_API_URL}/vendors`)
      .then((res) => res.json())
      .then((data) => setVendors(data));

    authFetch(`${process.env.REACT_APP_API_URL}/sites`)
      .then((res) => res.json())
      .then((data) => setSites(data));
  }, []);

  useEffect(() => {
    const boqItems = location.state?.boqItems;
    if (!Array.isArray(boqItems) || boqItems.length === 0) return;

    setItems(boqItems.map((item) => {
      const material = String(item.item_name || item.material || "").replace(/\u2063UOM\s*:[^—-]+\s*$/i, "").trim();
      const description = String(item.description || "").trim();
      const itemName = description && !material.includes(" - ")
        ? `${material} - ${description}`
        : material;

      return {
      item_name: itemName,
      brand: "",
      availableBrands: [],
      uom: item.uom || "Nos",
      quantity: item.qty ?? "",
      remarks: item.remarks || "",
      boq_item_id: item.id
    };
    }));
  }, [location.state]);

  // Filter here to only display 'Ongoing' site profiles across drop-down maps
  const ongoingSites = useMemo(() => {
    return sites.filter((s) => s.status === "Ongoing");
  }, [sites]);

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const handleSearch = async (value, index) => {
    updateItem(index, "item_name", value);

    if (!value.trim()) {
      setSuggestions((prev) => ({
        ...prev,
        [index]: [],
      }));
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/materials/search?q=${value}`);
      const data = await res.json();
      setSuggestions((prev) => ({
        ...prev,
        [index]: data,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        item_name: "",
        uom: "Nos",
        quantity: "",
      },
    ]);
  };

  const removeItem = (index) => {
    const updated = items.filter((_, i) => i !== index);
    setItems(updated);
  };
  const handleReferenceUpload = (e) => {
  const files = Array.from(e.target.files);

  const uploaded = files.map((file) => ({
    file,
    name: file.name,
    size: (file.size / 1024 / 1024).toFixed(2),
    type: file.name.split(".").pop().toUpperCase(),
    url: URL.createObjectURL(file),
  }));

  setReferenceFiles((prev) => [...prev, ...uploaded]);
};

const removeReferenceFile = (index) => {
  setReferenceFiles((prev) =>
    prev.filter((_, i) => i !== index)
  );
};

  const toggleVendor = (vendorId) => {
    if (selectedVendors.includes(vendorId)) {
      setSelectedVendors(selectedVendors.filter((v) => v !== vendorId));
    } else {
      setSelectedVendors([...selectedVendors, vendorId]);
    }
  };

  const filteredVendors = useMemo(() => {
  const filtered = vendors.filter((vendor) =>
    vendor.name?.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  // If search is empty, just slice the first 5 records for a clean view
  if (!vendorSearch.trim()) {
    return filtered.slice(0, 5);
  }

  return filtered;
}, [vendors, vendorSearch]);

  const handleCreateRFQ = async () => {
    if (!site) {
      alert("Please select site");
      return;
    }
    if (selectedVendors.length === 0) {
      alert("Select atleast one vendor");
      return;
    }

    try {
      const formData = new FormData();

formData.append("site_id", site);

formData.append(
  "vendors",
  JSON.stringify(selectedVendors)
);

const itemsWithoutImages = items.map((item) => ({
  item_name: item.item_name,
  brand: item.brand,
  uom: item.uom,
  quantity: item.quantity,
}));

formData.append(
  "items",
  JSON.stringify(itemsWithoutImages)
);

formData.append("cc_emails", ccEmails);

items.forEach((item, index) => {
  if (item.image) {
    formData.append(`image_${index}`, item.image);
  }
});

referenceFiles.forEach((file) => {
  formData.append("referenceFiles", file.file);
});

const res = await authFetch(
  `${process.env.REACT_APP_API_URL}/create-rfq`,
  {
    method: "POST",
    body: formData,
  }
);

      const data = await res.json();
      console.log(data);
      alert("RFQ Created Successfully ✅");
    } catch (err) {
      console.log(err);
      alert("Error creating RFQ");
    }
  };

  return (
    <div className="rfq-page" style={s.page}>
      <div style={s.wrap}>
        
        {/* HEADER */}
        <div style={s.header}>
          <div>
            <div style={s.title}>Create RFQ</div>
            <div style={s.sub}>Request commercial quotations efficiently from your verified vendor network</div>
          </div>
        </div>

        {/* SITE DETAILS */}
        <div style={s.section}>
          <div style={s.sectionHead}>
            <div>
              <div style={s.sectionTitle}>
                <Building2 size={18} color={t.primary} />
                Project Site Details
              </div>
              <div style={s.sectionSub}>Select destination commercial or office site workspace</div>
            </div>
          </div>

          <div style={s.body}>
            <div style={{ maxWidth: "900px" }}>
              <label style={s.label}>Destination Site Location</label>
              <select
                value={site}
                onChange={(e) => setSite(e.target.value)}
                style={s.select}
              >
                <option value="">Choose Site</option>
                {ongoingSites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.site_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* MATERIALS CONFIGURATOR */}
        <div style={s.section}>
          <div style={s.sectionHead}>
            <div>
              <div style={s.sectionTitle}>
                <Package2 size={18} color={t.primary} />
                Materials & Quantities Line Items
              </div>
              <div style={s.sectionSub}>Add your interior structural items</div>
            </div>

            <button onClick={addItem} style={s.primaryBtn}>
              <Plus size={16} />
              Add Line Item
            </button>
          </div>

          <div style={s.body}>
            <div style={s.tableWrapper}>
              
              {/* Table Column Headings */}
              <div className="rfq-item-header" style={s.tableHeader}>
                <div>ID</div>
                <div>Material Specification</div>
                <div>Brand</div>
                <div>UOM</div>
                <div>Qty</div>
                <div>Image</div>
                <div style={{ textAlign: "center" }}>Action</div>
              </div>

              {/* Scroll Window Frame */}
              <div className="rfq-item-list" style={s.scrollContainer}>
                {items.map((item, index) => (
                  <div key={index} className="rfq-item-card" style={{
                    ...s.tableRow,
                    borderBottom: index === items.length - 1 ? "none" : `1px solid ${t.borderLight}`
                  }}>
                    
                    {/* Index Format Matching Dashboard Layout */}
                    <div style={s.rowNumber}>
                      #{String(index + 1).padStart(2, '0')}
                    </div>

                    {/* Material Field Column with Dynamic suggestions layer */}
                    {/* Material Name (Textarea) */}
<div style={{ position: "relative", minWidth: 0 }}>
  <input
  value={splitSpecification(item.item_name).itemName}
  placeholder="Item name"
  onChange={(e) => {
      const description = splitSpecification(item.item_name).description;
      handleSearch(description ? `${e.target.value} - ${description}` : e.target.value, index);
  }}
  style={{
    ...s.compactInput,
    minHeight: "30px",
    fontSize: "13px",
    fontWeight: "600",
    padding: "5px 8px",
    borderRadius: "6px 6px 0 0"
  }}
/>
  <textarea
    rows={1}
    value={splitSpecification(item.item_name).description}
    placeholder="Description"
    onChange={(e) => {
      const itemName = splitSpecification(item.item_name).itemName;
      updateItem(index, "item_name", e.target.value ? `${itemName} - ${e.target.value}` : itemName);
      e.currentTarget.style.height = "auto";
      e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
    }}
    onInput={(e) => { e.currentTarget.style.height = "auto"; e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`; }}
    style={{ ...s.compactInput, minHeight: "24px", padding: "4px 8px", fontSize: "11px", lineHeight: "16px", color: t.muted, resize: "none", overflow: "hidden", borderTop: "none", borderRadius: "0 0 6px 6px" }}
  />

                      {/* Fixed Coordinates Layer: Floats over table container limits perfectly */}
                      {suggestions[index]?.length > 0 && (
                        <div
                          style={{
                            position: "fixed",
                            top: `${document.getElementById(`input-row-${index}`)?.getBoundingClientRect().bottom + 4}px`,
                            left: `${document.getElementById(`input-row-${index}`)?.getBoundingClientRect().left}px`,
                            width: `${document.getElementById(`input-row-${index}`)?.getBoundingClientRect().width}px`,
                            background: "#ffffff",
                            border: `1px solid ${t.border}`,
                            borderRadius: "8px",
                            overflow: "hidden",
                            zIndex: 9999,
                            boxShadow: "0 10px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.03)",
                          }}
                        >
                          {suggestions[index]?.map((mat, i) => (
                            <div
                              key={i}
                              onClick={() => {
  const updated = [...items];

  updated[index].item_name = mat.item_name;
  updated[index].uom = mat.uom;

  const brands = mat.brands || [];

updated[index].availableBrands = brands;

updated[index].brand =
  brands.length === 1
    ? brands[0]
    : "";

  setItems(updated);

  setSuggestions((prev) => ({
    ...prev,
    [index]: [],
  }));
}}
                              style={{
                                padding: "10px 14px",
                                cursor: "pointer",
                                borderBottom: i === suggestions[index].length - 1 ? "none" : `1px solid ${t.borderLight}`,
                                transition: "background 0.1s",
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f1f5f9")}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                            >
                              <div style={{ fontWeight: "600", fontSize: "13px", color: t.text }}>
                                {mat.item_name}
                              </div>
                              {mat.brand && (
                                <div style={{ fontSize: "11px", color: t.muted, marginTop: "2px" }}>
                                  {mat.brand} • {mat.uom}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
  <select value={item.brand} onChange={(e) => updateItem(index, "brand", e.target.value)} style={s.compactSelect}>
  <option value="">Select Brand</option>
  {item.availableBrands?.map((b) => <option key={b} value={b}>{b}</option>)}
</select>
</div>

                    {/* Compact Dropdown */}
                    <div>
                      <select
                        value={item.uom}
                        onChange={(e) => updateItem(index, "uom", e.target.value)}
                        style={s.compactSelect}
                      >
                        <option value="Nos">Nos</option>
                        <option value="KG">KG</option>
                        <option value="LTR">LTR</option>
                        <option value="MTRS">MTRS</option>
                        <option value="RMTR">RMTR</option>
                        <option value="SQFT">SQFT</option>
                        <option value="LOT">Lot</option>
                        <option value="RFT">RFT</option>
                        <option value="SQMTR">SQMTR</option>
                        <option value="LOAD">LOAD</option>
                        <option value="LS">LS</option>
                        <option value="ROLL">ROLL</option>
                      </select>
                    </div>

                    {/* Numeric Value Input */}
                    <input type="number" value={item.quantity} onChange={(e) => updateItem(index, "quantity", e.target.value)} style={{ ...s.compactInput, textAlign: "center" }} />

                    {/* Image Upload */}
<div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
  <label style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center" }}>
    {item.preview ? (
      <img src={item.preview} style={{ width: "38px", height: "38px", borderRadius: "4px", objectFit: "cover" }} />
    ) : (
      <div style={{ fontSize: "11px", color: t.primary, border: `1px dashed ${t.border}`, padding: "4px 8px", borderRadius: "4px" }}>Upload</div>
    )}
    <input type="file" hidden onChange={(e) => {
      const file = e.target.files[0];
      if (file) {
        const updated = [...items];
        updated[index].image = file;
        updated[index].preview = URL.createObjectURL(file);
        setItems(updated);
      }
    }} />
  </label>
</div>

                    {/* Action */}
<div style={{ display: "flex", justifyContent: "center" }}>
  {items.length > 1 && (
    <button onClick={() => removeItem(index)} style={s.compactRemoveBtn}>
      <Trash2 size={16} />
    </button>
  )}
</div>

                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
{/* =======================================================
        PROJECT REFERENCE DOCUMENTS
======================================================= */}

<div style={s.section}>

    <div style={s.sectionHead}>

        <div>

            <div style={s.sectionTitle}>
                📎 Project Reference Documents
            </div>

            <div style={s.sectionSub}>
                Upload Layouts, Drawings, BOQ, CAD Files and Specifications
            </div>

        </div>

    </div>

    <div style={s.body}>

        <label
            style={{
                border: "2px dashed #CBD5E1",
                borderRadius: 12,
                padding: 35,
                display: "block",
                textAlign: "center",
                cursor: "pointer",
                background: "#F8FAFC"
            }}
        >

            <input
                type="file"
                hidden
                multiple
                accept=".pdf,.dwg,.dxf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar"
                onChange={handleReferenceUpload}
            />

            <div style={{fontSize:40}}>
                📎
            </div>

            <div
                style={{
                    fontWeight:700,
                    marginTop:10
                }}
            >
                Click to Upload Reference Files
            </div>

            <div
                style={{
                    color:"#64748B",
                    marginTop:6,
                    fontSize:13
                }}
            >
                PDF • DWG • DXF • DOC • XLS • JPG • PNG • ZIP
            </div>

        </label>

        {
            referenceFiles.length > 0 && (

                <div
                    style={{
                        marginTop:25
                    }}
                >

                    {

                        referenceFiles.map((file,index)=>(

                            <div
                                key={index}

                                style={{

                                    display:"flex",

                                    justifyContent:"space-between",

                                    alignItems:"center",

                                    padding:"14px 18px",

                                    border:"1px solid #E2E8F0",

                                    borderRadius:10,

                                    marginBottom:10

                                }}

                            >

                                <div>

                                    <div
                                        style={{
                                            fontWeight:600
                                        }}
                                    >
                                        📄 {file.name}
                                    </div>

                                    <div
                                        style={{
                                            fontSize:12,
                                            color:"#64748B"
                                        }}
                                    >
                                        {file.type} • {file.size} MB
                                    </div>

                                </div>

                                <div
                                    style={{
                                        display:"flex",
                                        gap:15,
                                        alignItems:"center"
                                    }}
                                >

                                    <a

                                        href={file.url}

                                        target="_blank"

                                        rel="noreferrer"

                                        style={{
                                            color:"#2563EB",
                                            textDecoration:"none",
                                            fontWeight:600
                                        }}

                                    >

                                        View

                                    </a>

                                    <button

                                        onClick={()=>removeReferenceFile(index)}

                                        style={{

                                            border:"none",

                                            background:"transparent",

                                            color:"red",

                                            cursor:"pointer"

                                        }}

                                    >

                                        Remove

                                    </button>

                                </div>

                            </div>
                        ))
                    }
                </div>
            )
        }
    </div>

</div>
        {/* VENDOR DISTRIBUTION NETWORK */}
        <div style={s.section}>
          <div style={s.sectionHead}>
            <div>
              <div style={s.sectionTitle}>
                <ClipboardList size={18} color={t.primary} />
                Target Distribution Vendors
              </div>
              <div style={s.sectionSub}>Broadcast this RFQ matching the selected pipeline profiles</div>
            </div>

            <div style={{ fontSize: "12px", fontWeight: "700", color: t.primary, background: "#eff6ff", padding: "4px 10px", borderRadius: "6px" }}>
              {selectedVendors.length} Profiles Selected
            </div>
          </div>

          <div style={s.body}>
            <div style={{ position: "relative", marginBottom: "20px" }}>
              <Search
                size={16}
                style={{
                  position: "absolute",
                  left: "14px",
                  top: "11px",
                  color: t.muted,
                }}
              />
              <input
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
                placeholder="Search vendor enterprise partners by firm name..."
                style={{
                  ...s.input,
                  paddingLeft: "42px",
                }}
              />
            </div>

            <div style={s.vendorGrid}>
              {filteredVendors.map((vendor) => {
                const isSelected = selectedVendors.includes(vendor.id);

                return (
                  <div
                    key={vendor.id}
                    onClick={() => toggleVendor(vendor.id)}
                    style={{
                      ...s.vendorCard,
                      border: isSelected ? `2px solid ${t.primary}` : `1px solid ${t.border}`,
                      background: isSelected ? "#f0f6ff" : "#ffffff",
                      boxShadow: isSelected ? "0 4px 12px rgba(37,99,235,0.04)" : "0 1px 2px rgba(0,0,0,0.01)",
                    }}
                  >
                    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                      <div
                        style={{
                          ...s.vendorAvatar,
                          background: isSelected ? t.primary : "#e2e8f0",
                          color: isSelected ? "#ffffff" : "#475569",
                        }}
                      >
                        {vendor.name?.substring(0, 2).toUpperCase()}
                      </div>

                      <div>
                        <div style={{ fontWeight: "700", color: t.text, fontSize: "14px" }}>
                          {vendor.name}
                        </div>
                        <div style={{ fontSize: "12px", color: t.muted, marginTop: "2px" }}>
                          {vendor.contact_person || "Enterprise Supplier"}
                        </div>
                      </div>
                    </div>

                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      style={s.checkbox}
                    />
                  </div>
                );
              })}
            </div>
            <label style={{ display: "block", marginTop: "20px", fontSize: "12px", fontWeight: "700", color: t.text }}>
              CC Emails <span style={{ color: t.muted, fontWeight: "500" }}>(optional)</span>
              <input
                type="text"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="Enter one or more emails separated by commas"
                style={{ ...s.input, marginTop: "7px" }}
              />
            </label>
          </div>
        </div>

        {/* BOTTOM SUMMARY BAR */}
        <div className="rfq-bottom-bar" style={s.bottomBar}>
          <div>
            <div style={{ fontSize: "15px", fontWeight: "700", letterSpacing: "-0.01em" }}>RFQ Package Blueprint Summary</div>
            <div style={{ marginTop: "4px", color: "#94a3b8", fontSize: "12px", fontWeight: "500" }}>
              {items.length} Structural Item Line{items.length > 1 ? "s" : ""} • Routing directly to {selectedVendors.length} Selected Vendor Firm{selectedVendors.length !== 1 ? "s" : ""}
            </div>
          </div>

          <button onClick={handleCreateRFQ} style={s.submitBtn}>
            Dispatch & Create RFQ
          </button>
        </div>

      </div>
    </div>
  );
}

export default RFQPage;
